import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { TemplateBlock } from "@/components/content/TemplateBlock";
import { Checklist } from "@/components/content/Checklist";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Image, DollarSign, Workflow, Phone } from "lucide-react";

const faqs = [
  {
    question: "What landscaping service pages should I create?",
    answer: "Create pages for: landscape design, patio installation, retaining walls, outdoor kitchens, planting/softscaping, drainage solutions, and landscape maintenance. Add pages for any specialty services you offer."
  },
  {
    question: "Should landscaping service pages include pricing?",
    answer: "Include starting prices and project ranges. 'Paver patios starting at $15/sq ft' or 'Most patio projects: $8,000-$25,000' helps pre-qualify leads and sets expectations."
  },
  {
    question: "How important are photos on service pages?",
    answer: "Critical for landscaping. Include 3-5 relevant project photos per service page. Before/after comparisons work especially well for transformative services."
  },
  {
    question: "How do I optimize service pages for local SEO?",
    answer: "Include your city/county in the H1, title tag, and naturally throughout content. Add schema markup, embed a Google Map, and link to service area pages."
  }
];

const LandscapingServicePageTemplate = () => {
  const pageUrl = "https://www.leafrushmarketing.com/website-templates/landscaping-service-page-template/";
  
  return (
    <>
      <SEO
        title="Landscaping Service Page Template | Convert More Project Inquiries"
        description="Free landscaping service page template. Structure for hardscape, design, and installation pages that rank and convert."
        url={pageUrl}
        keywords="landscaping service page template, hardscape page template, landscape website pages"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Landscaping Service Page Template"
        pageDescription="Service page template for landscaping company websites."
        faqs={faqs}
      />

      <TopCTA />

      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">Website Template</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Landscaping{" "}
              <span className="text-primary">Service Page Template</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Create service pages for hardscaping, design, and installation that 
              showcase your expertise and generate project inquiries.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-2xl font-bold">Service Page Structure</h2>
            
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Section 1: SEO Header & Introduction
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="H1 Headline Examples"
                    content={`[Service] in [City] – Custom Design & Professional Installation

Examples:
• Patio Installation in Austin – Custom Paver & Stone Patios
• Retaining Walls in [City] – Functional & Beautiful Solutions
• Landscape Design in [City] – Transform Your Outdoor Space
• Outdoor Kitchens in [City] – Bring Your Backyard to Life`}
                  />
                  <TemplateBlock
                    title="Opening Paragraph"
                    content={`Looking for professional [service] in [City]? [Company Name] specializes in custom [service] that transforms outdoor spaces. From initial design to final installation, we handle every detail to create [benefit] that you'll enjoy for years.`}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="h-5 w-5 text-primary" />
                    Section 2: Project Gallery
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Checklist
                    title="Gallery Section Requirements"
                    items={[
                      "3-6 high-quality project photos",
                      "Mix of project sizes/styles",
                      "Before/after comparisons when available",
                      "Brief captions with materials used",
                      "Link to full portfolio"
                    ]}
                  />
                  <TemplateBlock
                    title="Gallery Headline"
                    content={`Our [Service] Projects

Browse recent [service] projects in [County]. Each project is custom-designed for the property and homeowner's vision.

[View Full Portfolio Button]`}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Workflow className="h-5 w-5 text-primary" />
                    Section 3: Our Process
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Process Section"
                    content={`Our [Service] Process

1. Consultation & Site Visit
   We visit your property to discuss your vision, assess the site, and understand your needs.

2. Design & Proposal
   Our team creates a detailed design and provides a comprehensive proposal with materials and pricing.

3. Installation
   Our experienced crews handle all installation, keeping you informed throughout the project.

4. Final Walkthrough
   We complete a walkthrough together, ensuring every detail meets your expectations.`}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Section 4: Investment & Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Pricing Section"
                    content={`[Service] Investment

Every [service] project is unique. Here's what to expect:

• Small projects: $[X,000] - $[X,000]
• Medium projects: $[X,000] - $[X,000]
• Large/complex projects: $[X,000]+

Factors that affect pricing:
• Size and scope of the project
• Materials selected (pavers, natural stone, concrete)
• Site conditions and access
• Design complexity

[Schedule Free Consultation Button]`}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-primary" />
                    Section 5: CTA & Contact
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Bottom CTA"
                    content={`Ready to Start Your [Service] Project?

Schedule your free design consultation. We'll visit your property, discuss your vision, and provide a detailed proposal.

[Schedule Consultation Button] [Call (XXX) XXX-XXXX Button]

Serving [City], [City 2], [City 3], and throughout [County].`}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <MidPageCTA />

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">DIY vs. Done-For-You</h2>
            <DIYvsDoneForYou
              diyPoints={[
                "Create service pages using this template",
                "Add your project photos and pricing",
                "Time investment: 3-5 hours per page",
                "Good if you have strong project photos"
              ]}
              doneForYouPoints={[
                "Professional copywriting and SEO",
                "Gallery optimization and compression",
                "Schema markup for rich results",
                "All pages included in subscription"
              ]}
            />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="Related Templates"
              links={[
                { to: "/website-templates/landscaping-homepage-template/", label: "Landscaping Homepage Template" },
                { to: "/website-templates/service-area-page-template/", label: "Service Area Page Template" },
                { to: "/website-templates/landscaping-website-seo-checklist/", label: "Landscaping SEO Checklist" },
                { to: "/service-pages-that-rank/", label: "Service Pages That Rank" },
                { to: "/website-templates/", label: "All Templates" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <BottomCTA />
      <CalendlyEmbed />
    </>
  );
};

export default LandscapingServicePageTemplate;
